<template>
  <div class="notification-bell" @click="toggleDropdown">
    <div class="bell-icon">
      🔔
      <span v-if="$hasUnreadNotifications" class="badge">
        {{ $formattedUnreadCount }}
      </span>
    </div>
    
    <!-- Dropdown -->
    <div v-if="showDropdown" class="notification-dropdown" @click.stop>

      <!-- Header -->
      <div class="dropdown-header">
        <h4>Notifications</h4>
        <div class="header-tabs">
          <button :class="{ active: selectedStatus === 'all' }" @click="selectedStatus = 'all'; filterChanged()">All</button>
          <button :class="{ active: selectedStatus === 'unread' }" @click="selectedStatus = 'unread'; filterChanged()">Unread</button>
        </div>
      </div>

      <!-- Actions bar -->
      <div class="dropdown-actions">
        <button @click="markAllRead" :disabled="!$hasUnreadNotifications">
          <i class="ti ti-checks"></i> Mark All Read
        </button>
        <button @click="clearRead">
          <i class="ti ti-trash"></i> Clear Read
        </button>
      </div>

      <!-- Category filter -->
      <div class="category-filter" v-if="selectedCategory !== null">
        <select v-model="selectedCategory" @change="filterChanged">
          <option value="">All Categories</option>
        </select>
      </div>

      <!-- Notification list -->
      <div class="notification-list" v-if="!$notificationsLoading">
        <div v-if="$notifications.length === 0" class="empty-state">
          <div class="empty-icon">
            <i class="ti ti-bell-off"></i>
          </div>
          <p>No notifications yet</p>
        </div>
        
        <div 
          v-for="notification in $notifications" 
          :key="notification.id" 
          class="notification-item" 
          :class="{ 'unread': !notification.is_read }" 
          @click="handleNotificationClick(notification)"
        >
          <!-- Avatar / Icon -->
          <div class="notification-avatar">
            <div class="avatar-circle" :style="{ background: notification.category_color || '#e8eaf0' }">
              <i class="ti" :class="getCategoryIcon(notification.category)"></i>
            </div>
          </div>

          <!-- Content -->
          <div class="notification-content">
            <div class="notification-top">
              <span class="notification-title">{{ notification.title }}</span>
              <span class="notification-time">{{ notification.created_at_human }}</span>
            </div>
            <p class="notification-message" v-html="notification.message"></p>
            <div class="notification-meta">
              <span class="notification-category" :style="{ color: notification.category_color }">
                {{ notification.category }}
              </span>
            </div>
          </div>

          <!-- Right actions -->
          <div class="notification-right">
            <!-- Unread dot -->
            <span v-if="!notification.is_read" class="unread-dot"></span>

            <!-- Action buttons (visible on hover) -->
            <div class="notification-actions">
              <button 
                @click.stop="markAsRead(notification.id)" 
                v-if="!notification.is_read" 
                class="action-btn action-read" 
                title="Mark as read"
              >
                <i class="ti ti-check"></i>
              </button>
              <button 
                @click.stop="deleteNotification(notification.id)" 
                class="action-btn action-delete" 
                title="Delete"
              >
                <i class="ti ti-x"></i>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Loading -->
      <div v-else class="loading-state">
        <div class="loading-spinner"></div>
        <p>Loading notifications...</p>
      </div>
      
      <!-- Footer -->
      <div class="dropdown-footer" v-if="$notifications.length > 0">
        <router-link to="/notifications" @click="closeDropdown">
          View All Notifications
        </router-link>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'NotificationBell',
  
  data() {
    return {
      showDropdown: false,
      selectedCategory: '',
      selectedStatus: 'all'
    }
  },

  methods: {
    toggleDropdown() {
      this.showDropdown = !this.showDropdown
      
      if (this.showDropdown) {
        this.$fetchNotifications({
          category: this.selectedCategory,
          status: this.selectedStatus
        })
      }
    },

    closeDropdown() {
      this.showDropdown = false
    },

    async markAllRead() {
      await this.$markAllNotificationsAsRead()
    },

    async clearRead() {
      await this.$clearReadNotifications()
    },

    async markAsRead(notificationId) {
      await this.$markNotificationAsRead(notificationId)
    },

    async deleteNotification(notificationId) {
      await this.$deleteNotification(notificationId)
    },

    async handleNotificationClick(notification) {
      await this.$handleNotificationClick(notification)
      this.closeDropdown()
    },

    async filterChanged() {
      await this.$fetchNotifications({
        category: this.selectedCategory,
        status: this.selectedStatus
      })
    },

    getCategoryIcon(category) {
      const map = {
        'DELIVERY_CHALLAN': 'ti-truck-delivery',
        'WORK_ORDER': 'ti-clipboard-list',
        'PAYMENT': 'ti-receipt-rupee',
        'INVOICE': 'ti-file-invoice',
        'LEAD': 'ti-user-check',
        'QUOTATION': 'ti-file-description',
        'SAMPLING': 'ti-cube-send',
        'STOCK': 'ti-stack-3',
      };
      return map[category] || 'ti-bell';
    }
  },

  mounted() {
    document.addEventListener('click', (e) => {
      if (!this.$el.contains(e.target)) {
        this.closeDropdown()
      }
    })
  }
}
</script>

<style scoped>
.notification-bell {
  position: relative;
  display: inline-block;
  cursor: pointer;
}

.bell-icon {
  position: relative;
  font-size: 15px;
  padding: 8px;
}

.badge {
  position: absolute;
  top: 0px;
  right: 0px;
  background: #ff4757;
  color: white;
  border-radius: 50%;
  padding: 3px 0px;
  font-size: 10px;
  font-weight: bold;
  min-width: 18px;
  text-align: center;
}

/* ===== Dropdown ===== */
.notification-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: -10px;
  width: 420px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 40px rgba(0, 0, 0, 0.12), 0 0 0 1px rgba(0, 0, 0, 0.04);
  z-index: 1000;
  overflow: hidden;
  animation: dropdownSlide 0.2s ease-out;
}

@keyframes dropdownSlide {
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
}

/* ===== Header ===== */
.dropdown-header {
  padding: 20px 20px 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dropdown-header h4 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a1d23;
}

.header-tabs {
  display: flex;
  gap: 4px;
  background: #f3f4f6;
  border-radius: 8px;
  padding: 3px;
}

.header-tabs button {
  border: none;
  background: transparent;
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 500;
  color: #6b7280;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.header-tabs button.active {
  background: #fff;
  color: #1a1d23;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
}

/* ===== Actions bar ===== */
.dropdown-actions {
  padding: 10px 20px;
  display: flex;
  gap: 8px;
  border-bottom: 1px solid #f3f4f6;
}

.dropdown-actions button {
  border: none;
  background: none;
  padding: 4px 10px;
  font-size: 12px;
  font-weight: 500;
  color: #6b7280;
  cursor: pointer;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 4px;
  transition: all 0.15s ease;
}

.dropdown-actions button:hover {
  background: #f3f4f6;
  color: #1a1d23;
}

.dropdown-actions button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.dropdown-actions button:disabled:hover {
  background: none;
  color: #6b7280;
}

/* ===== Category filter ===== */
.category-filter {
  padding: 8px 20px;
  border-bottom: 1px solid #f3f4f6;
  display: none;
}

.category-filter select {
  width: 100%;
  padding: 6px 10px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  font-size: 13px;
  color: #374151;
  background: #fafafa;
}

/* ===== Notification list ===== */
.notification-list {
  max-height: 380px;
  overflow-y: auto;
  scroll-behavior: smooth;
}

.notification-list::-webkit-scrollbar {
  width: 4px;
}

.notification-list::-webkit-scrollbar-track {
  background: transparent;
}

.notification-list::-webkit-scrollbar-thumb {
  background: #d1d5db;
  border-radius: 4px;
}

/* ===== Notification item ===== */
.notification-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px 20px;
  cursor: pointer;
  transition: background-color 0.15s ease;
  position: relative;
}

.notification-item:hover {
  background: #f9fafb;
}

.notification-item:not(:last-child) {
  border-bottom: 1px solid #f7f8f9;
}

.notification-item.unread {
  background: #fafbff;
}

.notification-item.unread:hover {
  background: #f0f4ff;
}

/* ===== Avatar ===== */
.notification-avatar {
  flex-shrink: 0;
}

.avatar-circle {
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.avatar-circle i {
  font-size: 18px;
  color: #fff;
}

/* ===== Content ===== */
.notification-content {
  flex: 1;
  min-width: 0;
}

.notification-top {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 2px;
}

.notification-title {
  font-size: 13.5px;
  font-weight: 600;
  color: #1a1d23;
  line-height: 1.3;
}

.notification-time {
  font-size: 11.5px;
  color: #9ca3af;
  white-space: nowrap;
  flex-shrink: 0;
  margin-top: 1px;
}

.notification-message {
  margin: 3px 0 0 0;
  font-size: 12.5px;
  color: #6b7280;
  line-height: 1.45;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.notification-meta {
  margin-top: 6px;
}

.notification-category {
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
}

/* ===== Right side ===== */
.notification-right {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  min-width: 24px;
}

.unread-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #22c55e;
  flex-shrink: 0;
}

/* ===== Action buttons ===== */
.notification-actions {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.action-btn {
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  transition: all 0.15s ease;
}

.action-read {
  background: #dcfce7;
  color: #16a34a;
}

.action-read:hover {
  background: #16a34a;
  color: #fff;
}

.action-delete {
  background: #fee2e2;
  color: #dc2626;
}

.action-delete:hover {
  background: #dc2626;
  color: #fff;
}

/* ===== Empty state ===== */
.empty-state {
  padding: 48px 20px;
  text-align: center;
}

.empty-icon {
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #f3f4f6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 12px;
}

.empty-icon i {
  font-size: 24px;
  color: #9ca3af;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
  color: #9ca3af;
  font-weight: 500;
}

/* ===== Loading ===== */
.loading-state {
  padding: 48px 20px;
  text-align: center;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #f3f4f6;
  border-top-color: #3b82f6;
  border-radius: 50%;
  animation: spin 0.7s linear infinite;
  margin: 0 auto 12px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.loading-state p {
  margin: 0;
  font-size: 13px;
  color: #9ca3af;
}

/* ===== Footer ===== */
.dropdown-footer {
  padding: 14px 20px;
  border-top: 1px solid #f3f4f6;
  text-align: center;
}

.dropdown-footer a {
  color: #3b82f6;
  text-decoration: none;
  font-size: 13.5px;
  font-weight: 600;
  transition: color 0.15s ease;
}

.dropdown-footer a:hover {
  color: #2563eb;
}
.notification-list {
  max-height: 380px;
  overflow-y: auto;
  scroll-behavior: smooth;
  position: relative;
}
.notification-list::after {
  content: '';
  position: sticky;
  bottom: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%);
  pointer-events: none;
  display: block;
}

/* ===== Responsive ===== */
@media (max-width: 480px) {
  .notification-dropdown {
    width: calc(100vw - 20px);
    right: -60px;
  }
}
</style>